import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ClickableLink from "@/components/ui/clickable-link";
import Text from "@/components/ui/text";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";

interface Props {
  partnerId: string | null;
  partnerName: string | null | undefined;
}

// Links a partner contact row to the partner it belongs to. TableNameCell is
// not reusable here — it links to the row's own entity, this cell links to a
// referenced one, so a left-click navigates instead of opening the row's
// quick view.
export const PartnerLinkCell: React.FC<Props> = ({
  partnerId,
  partnerName
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!partnerId) {
    return <>{t("tables.notAvailable")}</>;
  }

  // An unresolved name stays inert — a link labelled "not found" reads as a
  // dead end, and the partner it points at may itself be gone.
  if (!partnerName) {
    return <>{t("notFound")}</>;
  }

  const url = `/${entityTypeToEntityName[EntityType.PARTNER]}/${partnerId}`;

  return (
    <ClickableLink
      href={url}
      onClick={() => navigate(url)}
      className="text-inherit no-underline truncate"
    >
      <Text
        variant="body"
        className="truncate transition-all duration-300 ease-in-out hover:text-link"
        data-test={`${partnerId}-open-partner`}
      >
        {partnerName}
      </Text>
    </ClickableLink>
  );
};

export default PartnerLinkCell;
